//#region 1️⃣ Angular Imports
import { ErrorHandler, Injector, isDevMode, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
//#endregion
//#region 2️⃣ Ionic & Angular Material
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
//#endregion
//#region 3️⃣ Third-Party Libraries
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LetDirective, PushPipe } from '@ngrx/component';
import { AgGridModule } from 'ag-grid-angular';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { NgxPaginationModule } from 'ngx-pagination';
//#endregion
//#region 4️⃣ Firebase
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  getAuth,
  indexedDBLocalPersistence,
  provideAuth,
  setPersistence,
} from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
//#endregion
//#region 5️⃣ Environment Config
import { environment } from '../environments/environment';
//#endregion
//#region 6️⃣ Application Root
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
//#endregion
//#region 7️⃣ Features
import { CoreModule } from './features/core/core.module';
import { MaintenanceModalComponent } from './features/maintenance/components/maintenance-modal/maintenance-modal.component';
//#endregion
//#region 8️⃣ Shared Modules
import { IconBackspaceOutlineComponent } from './shared-modules/shared-icons/components/icon-backspace-outline/icon-backspace-outline.component';
import { SharedIconsModule } from './shared-modules/shared-icons/shared-icons.module';
import { SharedModule } from './shared-modules/shared-module/shared-module.module';
import { SharedComponentsModule } from './shared/shared-components/shared-components.module';
import { UiComponentsModule } from './shared/shared-module-ui/ui-components.module';
//#endregion
//#region 9️⃣ Global Services or Handlers
import { SettingsNewModule } from './features/settings-new/settings-new-module';
import { GlobalErrorHandler } from './shared/shared-models/error/global-error-handler';
//#endregion

//#region 🛠️ Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

//#endregion

@NgModule({
  declarations: [AppComponent, MaintenanceModalComponent],
  bootstrap: [AppComponent],
  imports: [
    //#region 1️⃣ Angular & Core
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    //#endregion

    //#region 2️⃣ Ionic & Angular Material
    MatTooltipModule,
    MatDialogModule,
    //#endregion

    //#region 3️⃣ Third-Party Libraries
    AgGridModule,
    NgxPaginationModule,
    PushPipe,
    LetDirective,
    //#endregion

    //#region 4️⃣ PWA
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    //#endregion

    //#region 8️⃣ Shared Modules
    SharedComponentsModule,
    SharedIconsModule,
    SharedModule,
    UiComponentsModule,
    //#endregion

    //#region 7️⃣ Features
    CoreModule,
    SettingsNewModule,
    //#endregion
  ],
  providers: [
    //#region 4️⃣ Firebase
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => {
      const auth = getAuth();
      setTimeout(() => {
        setPersistence(auth, indexedDBLocalPersistence).catch((error: Error) => {
          console.error('Failed to set Firebase Auth persistence:', error);
        });
      }, 0);
      return auth;
    }),
    provideStorage(() => getStorage()),
    provideFunctions(() => getFunctions(getApp(), 'africa-south1')),
    //#endregion

    //#region 9️⃣ Global Services
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideHttpClient(withInterceptorsFromDi()),
    //#endregion
  ],
})
export class AppModule {
  constructor(injector: Injector) {
    // Convert Angular component to a native custom element
    const BackspaceEl: CustomElementConstructor = createCustomElement(
      IconBackspaceOutlineComponent,
      { injector },
    );

    if (!customElements.get('app-icon-backspace-outline')) {
      customElements.define('app-icon-backspace-outline', BackspaceEl);
    }
  }
}
